import Phaser from "phaser";
import {
  applyMove,
  createGame,
  DEFAULT_RULES,
  findAllGroups,
  revealFraction,
  Rng,
  type Cell,
  type GameState,
  type LevelDef,
  type MoveResult,
} from "@enjoyment/core";
import { exposeBotHook } from "@kit/botHook";
import { afterSkip, afterWin, mapText, openMap, starLine } from "@kit/map";
import { recordSkip, recordWin } from "@kit/progress";
import { classicLevel, classicStars } from "../classic";
import { MAP_ID } from "../saga";
import { lang, t } from "../i18n";
import { track, trackExposure } from "../telemetry";
import { hdPicturesVariant } from "../experiments";
import { loadHdPicture } from "@shared/art/pictures";
import { loadProgress, saveProgress, type Progress } from "../progress";
import { resolveImage, revealOrder, type PixelImage } from "../art/pixelImage";
import { W, backdrop } from "@kit/ui";
import { isMuted, setMuted, sfx, unlockAudio } from "@shared/audio";

export const COLORS = [0xff595e, 0xffca3a, 0x8ac926, 0x1982c4, 0x6a4c93, 0xff924c];
export const FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const BOTTOM_BAR = 110; // hint area height, anchored to the real screen bottom
const BOARD_MAX_W = 680;
const IDLE_HINT_MS = 6000; // a still board wiggles its biggest group after this long
const BONUS_SHOTS = 8; // at most this many leftover moves fly onto the board as fireworks on a win
const FLY_MAX = 14; // goal tiles flying to their chip per move

type Tile = Phaser.GameObjects.Image;

/** Scene data for Klasik: a 1-based level from the level map. */
export interface GameStart {
  level?: number;
}

/**
 * Scale.EXPAND fits the 720-wide design to the parent and grows the *other* axis. On parents wider
 * than 9:16 (tablets, desktop column, clip renderer) the logical width exceeds 720; scrolling the
 * camera by half the surplus keeps the 720-wide layout centered. Input uses world coords, so taps stay right.
 */
export function centerCamera(scene: Phaser.Scene): void {
  scene.cameras.main.setScroll(-Math.max(0, scene.scale.width - W) / 2, 0);
}

export class GameScene extends Phaser.Scene {
  protected boardTop = 430;
  protected progress!: Progress;
  /** 0-based index of the level being played (the map can reopen any cleared level). */
  protected index = 0;
  private levelArg?: number;
  protected level!: LevelDef;
  protected state!: GameState;
  protected rng!: Rng;
  protected tiles: (Tile | null)[][] = [];
  protected cell = 80;
  protected ox = 0;
  protected oy = 0;
  protected locked = false;
  protected image!: PixelImage;
  protected order: number[] = [];
  protected revealGfx!: Phaser.GameObjects.Graphics;
  /** HD picture experiment: the full-resolution picture sits between the frame and the block covers. */
  protected coverGfx?: Phaser.GameObjects.Graphics;
  protected hdView?: Phaser.GameObjects.Image;
  protected hdImg?: HTMLImageElement;
  private hdWanted?: string;
  protected hudMoves!: Phaser.GameObjects.Text;
  protected hudLevel!: Phaser.GameObjects.Text;
  protected goalTexts: Phaser.GameObjects.Text[] = [];
  protected goalChips: Phaser.GameObjects.Image[] = [];
  protected overlay?: Phaser.GameObjects.Container;
  protected hint!: Phaser.GameObjects.Text;
  protected startedAt = 0;
  protected lastDone: number[] = [];
  protected muteBtn!: Phaser.GameObjects.Text;
  private keyboardCursor?: Phaser.GameObjects.Rectangle;
  private keyboardRow = 0;
  private keyboardCol = 0;
  private keyboardActive = false;
  /** Special previews: badges on tiles whose group would make a rocket or a bomb. */
  private markers: Phaser.GameObjects.Image[] = [];
  private idleHint?: Phaser.Time.TimerEvent;
  private hinted: Tile[] = [];
  /** Classic picture frame; it grows into the spare height of tall phones (the picture is the reward). */
  protected pic = { x: 60, y: 180, size: 220 };
  private panelBg?: Phaser.GameObjects.Rectangle;
  private goalLabel?: Phaser.GameObjects.Text;

  constructor(key = "game") {
    super(key);
  }

  /** `{ level }` (1-based) comes from the level map; without it the next unplayed level opens. */
  init(data?: GameStart): void {
    this.levelArg = data?.level;
  }

  create(): void {
    backdrop(this, { light: 0xffca3a });
    this.progress = loadProgress();
    this.makeTextures();
    this.buildHud();
    this.startLevel(this.levelArg ? this.levelArg - 1 : this.progress.levelIndex);
    // ?bot=1: read-only state for the level-map smoke (tools/video-factory/map-smoke.mjs).
    // tap(r, c) plays a cell like a player (smokes, screenshots); markers = special badges on the board.
    exposeBotHook({
      state: () => ({ level: this.index + 1, movesLeft: this.state.movesLeft, status: this.state.status, locked: this.locked, overlay: !!this.overlay, markers: this.markers.length, grid: this.state.grid, goals: this.state.progress }),
      tap: (r: number, c: number) => this.onTap({ worldX: this.px(c), worldY: this.py(r) } as Phaser.Input.Pointer),
      // Slow motion for frame captures (headless screenshots take ~0.4 s each).
      timeScale: (k: number) => void (this.tweens.timeScale = this.time.timeScale = k),
      // Video factory (tools/video-factory/clip.mts): the full core state for the greedy bot, the picture.
      game: () => this.state,
      picture: () => ({ id: this.image.sourceId ?? this.level.id, name: this.pictureName() ?? "" }),
    });
  }

  // ---------- textures ----------
  protected makeTextures(): void {
    const size = 96;
    const g = this.add.graphics();
    const rounded = (color: number) => {
      g.clear();
      g.fillStyle(color, 1);
      g.fillRoundedRect(4, 4, size - 8, size - 8, 18);
      g.fillStyle(0xffffff, 0.22);
      g.fillRoundedRect(12, 10, size - 24, 26, { tl: 12, tr: 12, bl: 6, br: 6 });
      g.fillStyle(0x000000, 0.18);
      g.fillRoundedRect(10, size - 22, size - 20, 12, 6);
    };
    COLORS.forEach((c, i) => {
      rounded(c);
      g.generateTexture(`c${i}`, size, size);
    });
    // rocket h / v
    for (const dir of ["h", "v"] as const) {
      g.clear();
      g.fillStyle(0xf1f1f6, 1);
      g.fillRoundedRect(4, 4, size - 8, size - 8, 18);
      g.fillStyle(0x2b2d42, 1);
      if (dir === "h") {
        g.fillRoundedRect(16, 36, 64, 24, 12);
        g.fillTriangle(80, 30, 80, 66, 92, 48);
        g.fillStyle(0xff595e, 1);
        g.fillRect(8, 40, 12, 16);
      } else {
        g.fillRoundedRect(36, 16, 24, 64, 12);
        g.fillTriangle(30, 16, 66, 16, 48, 4);
        g.fillStyle(0xff595e, 1);
        g.fillRect(40, 76, 16, 12);
      }
      g.generateTexture(`rocket_${dir}`, size, size);
    }
    // bomb
    g.clear();
    g.fillStyle(0xf1f1f6, 1);
    g.fillRoundedRect(4, 4, size - 8, size - 8, 18);
    g.fillStyle(0x2b2d42, 1);
    g.fillCircle(48, 54, 28);
    g.fillStyle(0xffca3a, 1);
    g.fillCircle(66, 24, 6);
    g.lineStyle(5, 0x2b2d42, 1);
    g.lineBetween(56, 34, 66, 24);
    g.fillStyle(0xffffff, 0.35);
    g.fillCircle(38, 44, 8);
    g.generateTexture("bomb", size, size);
    // preview badges (drawMarkers): a round coin, so they read as "this group makes a special", not as a glitch
    for (const kind of ["rocket", "bomb"] as const) {
      g.clear();
      g.fillStyle(0x000000, 0.25).fillCircle(33, 35, 29);
      g.fillStyle(kind === "rocket" ? 0xffca3a : 0xff924c, 1).fillCircle(32, 32, 29);
      g.lineStyle(4, 0x2b2d42, 1).strokeCircle(32, 32, 27);
      g.fillStyle(0x2b2d42, 1);
      if (kind === "rocket") {
        g.fillRoundedRect(14, 26, 30, 12, 6);
        g.fillTriangle(42, 22, 42, 42, 52, 32);
        g.fillStyle(0xff595e, 1).fillRect(10, 28, 6, 8);
      } else {
        g.fillCircle(30, 36, 14);
        g.lineStyle(4, 0x2b2d42, 1).lineBetween(38, 26, 44, 18);
        g.fillStyle(0xffffff, 1).fillCircle(45, 16, 4);
        g.fillStyle(0xffffff, 0.4).fillCircle(25, 31, 4);
      }
      g.generateTexture(`mark_${kind}`, 64, 64);
    }
    // crates
    for (const hp of [1, 2]) {
      g.clear();
      g.fillStyle(hp === 2 ? 0x8d5524 : 0xc68642, 1);
      g.fillRoundedRect(4, 4, size - 8, size - 8, 10);
      g.lineStyle(6, 0x5c3a1e, 1);
      g.strokeRoundedRect(8, 8, size - 16, size - 16, 8);
      g.lineBetween(8, 8, size - 8, size - 8);
      g.lineBetween(size - 8, 8, 8, size - 8);
      if (hp === 2) {
        g.lineStyle(4, 0x3a2412, 1);
        g.strokeRoundedRect(18, 18, size - 36, size - 36, 6);
      }
      g.generateTexture(`crate${hp}`, size, size);
    }
    // particle
    g.clear();
    g.fillStyle(0xffffff, 1);
    g.fillRoundedRect(0, 0, 14, 14, 4);
    g.generateTexture("spark", 14, 14);
    g.destroy();
  }

  // ---------- HUD ----------
  protected buildHud(): void {
    this.buildTopBar();
    this.buildPanel();
    this.buildBottom();
  }

  /** Title, moves counter, mute, back to the level map. */
  protected buildTopBar(): void {
    this.add.rectangle(W / 2, 0, W * 3, 150, 0x12152b).setOrigin(0.5, 0);
    this.hudLevel = this.add
      .text(36, 44, "", { fontFamily: FONT, fontSize: "34px", color: "#ffffff", fontStyle: "bold" })
      .setOrigin(0, 0.5);
    this.add
      .text(36, 92, mapText("map", lang), { fontFamily: FONT, fontSize: "22px", color: "#8f96c2" })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        openMap(this, MAP_ID);
      });
    this.add
      .text(W - 36, 30, t("moves"), { fontFamily: FONT, fontSize: "22px", color: "#8f96c2" })
      .setOrigin(1, 0.5);
    this.hudMoves = this.add
      .text(W - 36, 84, "", { fontFamily: FONT, fontSize: "64px", color: "#ffffff", fontStyle: "bold" })
      .setOrigin(1, 0.5);

    this.muteBtn = this.add
      .text(W - 36, 132, isMuted() ? "🔇" : "🔊", { fontFamily: FONT, fontSize: "24px" })
      .setOrigin(1, 0.5)
      .setInteractive({ useHandCursor: true })
      .on("pointerdown", (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        setMuted(!isMuted());
        this.muteBtn.setText(isMuted() ? "🔇" : "🔊");
        if (!isMuted()) sfx.goalTick();
      });
  }

  /** Classic: reveal picture on the left, goals on the right. */
  protected buildPanel(): void {
    this.revealGfx = this.add.graphics();
    this.coverGfx = this.add.graphics();
    this.goalLabel = this.add.text(0, 200, t("goal"), { fontFamily: FONT, fontSize: "22px", color: "#8f96c2" }).setOrigin(0, 0.5);
    this.layoutPanel();
  }

  /** Classic panel: picture size from the spare height (220 on a 9:16 screen, up to 340), board below it. */
  protected layoutPanel(): void {
    if (!this.goalLabel) return;
    const size = Math.round(220 + Math.min(120, Math.max(0, this.scale.height - 1280) * 0.45));
    this.pic = { x: 60, y: 180, size };
    const h = size + 30;
    this.panelBg?.destroy();
    this.panelBg = this.add.rectangle(W / 2, 165 + h / 2, 660, h, 0x262b4d).setDepth(-1);
    this.goalLabel.setX(this.goalsX());
    this.boardTop = 165 + h + 15;
    this.hdView?.setPosition(this.pic.x, this.pic.y).setDisplaySize(size, size);
  }

  private goalsX(): number {
    return Math.max(W / 2 + 40, this.pic.x + this.pic.size + 40);
  }

  /** Hint line + board input + resize handling. */
  protected buildBottom(): void {
    this.hint = this.add
      .text(W / 2, this.scale.height - 55, t("tap_hint"), { fontFamily: FONT, fontSize: "22px", color: "#8f96c2" })
      .setOrigin(0.5);

    this.input.on("pointerdown", (p: Phaser.Input.Pointer) => this.onTap(p));
    const keyboard = this.input.keyboard;
    if (keyboard) {
      const onKeyDown = (event: KeyboardEvent) => {
        if (!this.level || !this.state || this.locked || this.overlay) return;
        if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || (event.target instanceof HTMLElement && event.target.isContentEditable)) return;
        const key = event.key;
        if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter", " "].includes(key)) return;
        event.preventDefault();
        if (key === "ArrowUp") this.keyboardRow = Math.max(0, this.keyboardRow - 1);
        else if (key === "ArrowDown") this.keyboardRow = Math.min(this.level.height - 1, this.keyboardRow + 1);
        else if (key === "ArrowLeft") this.keyboardCol = Math.max(0, this.keyboardCol - 1);
        else if (key === "ArrowRight") this.keyboardCol = Math.min(this.level.width - 1, this.keyboardCol + 1);
        this.keyboardActive = true;
        this.updateKeyboardCursor();
        if (key === "Enter" || key === " ") this.onTap({ worldX: this.px(this.keyboardCol), worldY: this.py(this.keyboardRow) } as Phaser.Input.Pointer, true);
      };
      keyboard.on("keydown", onKeyDown);
      this.events.once("shutdown", () => keyboard.off("keydown", onKeyDown));
    }
    centerCamera(this);
    const onResize = () => {
      centerCamera(this);
      if (!this.level) return;
      this.hint.setY(this.scale.height - 55);
      this.layoutPanel();
      this.layoutBoard();
      for (let r = 0; r < this.level.height; r++)
        for (let c = 0; c < this.level.width; c++) {
          const tl = this.tiles[r]?.[c];
          if (tl) tl.setPosition(this.px(c), this.py(r)).setDisplaySize(this.cell, this.cell);
        }
      if (!this.locked) this.drawMarkers();
      if (this.state) this.updateHud();
      this.updateKeyboardCursor();
    };
    this.scale.on("resize", onResize);
    // ScaleManager is global: drop the listener when this scene goes away (map ↔ game).
    this.events.once("shutdown", () => this.scale.off("resize", onResize));
  }

  private updateKeyboardCursor(): void {
    if (!this.keyboardActive || !this.level) return;
    if (!this.keyboardCursor) {
      this.keyboardCursor = this.add.rectangle(0, 0, 1, 1, 0x000000, 0).setStrokeStyle(4, 0xffca3a, 1).setDepth(100);
    }
    this.keyboardCursor
      .setPosition(this.px(this.keyboardCol), this.py(this.keyboardRow))
      .setDisplaySize(this.cell * 0.92, this.cell * 0.92)
      .setVisible(true);
  }

  /**
   * HD picture experiment (config/experiments/mozaik-hd-pictures.json): log the exposure, and in the
   * "hd" arm load the picture's full-resolution twin and slot it under the block covers.
   * Hand-drawn sprites (no pack id) are outside the experiment.
   */
  protected useHdPicture(id: string | undefined, x: number, y: number, size: number): void {
    this.hdView?.destroy();
    this.hdView = undefined;
    this.hdImg = undefined;
    this.hdWanted = id;
    if (!id) return;
    trackExposure("classic");
    if (hdPicturesVariant() !== "hd") return;
    void loadHdPicture(id).then((img) => {
      if (!img || !this.sys.isActive() || this.hdWanted !== id) return;
      const key = `hd-${id}`;
      if (!this.textures.exists(key)) this.textures.addImage(key, img);
      this.hdImg = img;
      this.hdView = this.add.image(x, y, key).setOrigin(0, 0).setDisplaySize(size, size);
      this.children.moveAbove(this.hdView as never, this.revealGfx as never);
      if (this.coverGfx) this.children.moveAbove(this.coverGfx as never, this.hdView as never);
      this.drawReveal();
    });
  }

  protected drawReveal(): void {
    const g = this.revealGfx;
    g.clear();
    this.coverGfx?.clear();
    const { x: x0, y: y0, size } = this.pic;
    const px = size / this.image.size;
    g.fillStyle(0x1b1f3a, 1);
    g.fillRoundedRect(x0 - 8, y0 - 8, size + 16, size + 16, 12);
    const frac = revealFraction(this.state.progress);
    const shown = Math.round(frac * this.order.length);
    if (this.hdView && this.coverGfx) {
      // HD: the picture is already drawn; cover the cells that are not revealed yet.
      this.coverGfx.fillStyle(0x30365f, 1);
      for (let k = shown; k < this.order.length; k++) {
        const i = this.order[k]!;
        this.coverGfx.fillRect(x0 + (i % this.image.size) * px, y0 + Math.floor(i / this.image.size) * px, px + 0.5, px + 0.5);
      }
      return;
    }
    for (let k = 0; k < this.order.length; k++) {
      const i = this.order[k]!;
      const col = this.image.pixels[i]!;
      const x = i % this.image.size;
      const y = Math.floor(i / this.image.size);
      if (k < shown) {
        if (col >= 0) {
          g.fillStyle(Phaser.Display.Color.HexStringToColor(this.image.palette[col]!).color, 1);
          g.fillRect(x0 + x * px, y0 + y * px, px + 0.5, px + 0.5);
        }
      } else {
        g.fillStyle(0x30365f, 1);
        g.fillRect(x0 + x * px + 1, y0 + y * px + 1, px - 2, px - 2);
      }
    }
  }

  protected drawGoals(): void {
    this.goalTexts.forEach((x) => x.destroy());
    this.goalChips.forEach((x) => x.destroy());
    this.goalTexts = [];
    this.goalChips = [];
    this.state.progress.forEach((p, i) => {
      const y = 250 + i * 64;
      const key = p.goal.type === "color" ? `c${p.goal.color}` : "crate1";
      const chip = this.add.image(this.goalsX() + 24, y, key).setDisplaySize(48, 48);
      const left = Math.max(0, p.goal.count - p.done);
      if ((this.lastDone[i] ?? 0) < p.done && this.lastDone.length) {
        chip.setDisplaySize(64, 64);
        this.tweens.add({ targets: chip, displayWidth: 48, displayHeight: 48, duration: 220, ease: "Back.Out" });
      }
      const txt = this.add
        .text(this.goalsX() + 64, y, left === 0 ? "✓" : String(left), {
          fontFamily: FONT,
          fontSize: "36px",
          color: left === 0 ? "#8ac926" : "#ffffff",
          fontStyle: "bold",
        })
        .setOrigin(0, 0.5);
      this.goalChips.push(chip);
      this.goalTexts.push(txt);
    });
    this.lastDone = this.state.progress.map((p) => p.done);
  }

  protected updateHud(): void {
    this.hudMoves.setText(String(this.state.movesLeft));
    this.hudLevel.setText(t("level", { n: this.index + 1 }));
    this.drawGoals();
    this.drawReveal();
  }

  // ---------- level lifecycle ----------
  protected levelFor(index: number): LevelDef {
    return classicLevel(index);
  }

  protected startLevel(index: number): void {
    this.overlay?.destroy();
    this.overlay = undefined;
    this.index = index;
    // levelIndex stays the furthest level reached: replaying an old level from the map never rewinds it.
    this.progress.levelIndex = Math.max(this.progress.levelIndex, index);
    saveProgress(this.progress);
    this.level = this.levelFor(index);
    const { state, rng } = createGame(this.level);
    this.state = state;
    this.rng = rng;
    this.lastDone = [];
    this.image = resolveImage(this.level.imageId ?? this.level.id);
    this.order = revealOrder(this.image.size);
    this.useHdPicture(this.image.sourceId, this.pic.x, this.pic.y, this.pic.size);
    this.layoutBoard();
    this.buildTiles();
    this.updateHud();
    this.locked = false;
    this.keyboardRow = 0;
    this.keyboardCol = 0;
    this.keyboardActive = false;
    this.keyboardCursor?.setVisible(false);
    this.startedAt = Date.now();
    track("level_start", { level: this.level.id, index, difficulty: this.level.difficulty ?? "?" });
  }

  protected layoutBoard(): void {
    const { width, height } = this.level;
    const boardBottom = this.scale.height - BOTTOM_BAR;
    this.cell = Math.min(BOARD_MAX_W / width, (boardBottom - this.boardTop) / height, 88);
    this.ox = (W - this.cell * width) / 2;
    // The board sits towards the bottom (thumb reach).
    const extra = boardBottom - this.boardTop - this.cell * height;
    this.oy = boardBottom - this.cell * height - Math.min(24, extra / 2);
  }

  protected tileKey(cell: Cell): string {
    if (!cell) return "spark";
    if (cell.kind === "color") return `c${cell.color}`;
    if (cell.kind === "rocket") return `rocket_${cell.dir}`;
    if (cell.kind === "bomb") return "bomb";
    return `crate${Math.min(2, cell.hp)}`;
  }

  protected px(c: number): number {
    return this.ox + c * this.cell + this.cell / 2;
  }
  protected py(r: number): number {
    return this.oy + r * this.cell + this.cell / 2;
  }

  protected buildTiles(): void {
    for (const row of this.tiles) for (const tl of row) tl?.destroy();
    this.tiles = [];
    for (let r = 0; r < this.level.height; r++) {
      const row: (Tile | null)[] = [];
      for (let c = 0; c < this.level.width; c++) {
        row.push(this.makeTile(r, c, this.state.grid[r]![c] ?? null));
      }
      this.tiles.push(row);
    }
    this.drawMarkers();
    this.armIdleHint();
  }

  /** The level picture's name in the player's language (hand-made and pack pictures; procedural ones have none). */
  protected pictureName(): string | undefined {
    return lang === "en" ? (this.image.nameEn ?? this.image.name) : this.image.name;
  }

  // ---------- guidance ----------
  /**
   * Toon Blast-style preview: every tile of a group big enough for a special wears that special's badge,
   * so the player sees "tap here for a rocket" before tapping. Redrawn whenever the board settles.
   */
  protected drawMarkers(): void {
    this.clearMarkers();
    if (!this.state || this.state.status !== "playing" || this.overlay) return;
    const size = this.cell * 0.36;
    for (const group of findAllGroups(this.state.grid, DEFAULT_RULES.rocketAt)) {
      const key = group.length >= DEFAULT_RULES.bombAt ? "mark_bomb" : "mark_rocket";
      for (const p of group) {
        const m = this.add.image(this.px(p.c) + this.cell * 0.26, this.py(p.r) - this.cell * 0.26, key).setDisplaySize(size, size).setDepth(5);
        this.markers.push(m);
      }
    }
  }

  protected clearMarkers(): void {
    for (const m of this.markers) m.destroy();
    this.markers = [];
  }

  /** A still board nudges the player: after IDLE_HINT_MS the biggest group wiggles (again every interval). */
  protected armIdleHint(): void {
    this.idleHint?.remove();
    this.idleHint = this.time.delayedCall(IDLE_HINT_MS, () => this.showIdleHint());
  }

  protected stopIdleHint(): void {
    this.idleHint?.remove();
    this.idleHint = undefined;
    for (const tl of this.hinted) {
      if (!tl.active) continue;
      this.tweens.killTweensOf(tl);
      tl.setAngle(0);
    }
    this.hinted = [];
  }

  private showIdleHint(): void {
    if (this.locked || this.overlay || !this.state || this.state.status !== "playing") return;
    const groups = findAllGroups(this.state.grid, 2);
    if (groups.length) {
      const best = groups.reduce((a, b) => (b.length > a.length ? b : a));
      this.hinted = best.map((p) => this.tiles[p.r]?.[p.c]).filter((tl): tl is Tile => !!tl);
      for (const tl of this.hinted)
        this.tweens.add({ targets: tl, angle: { from: -5, to: 5 }, duration: 90, yoyo: true, repeat: 2, onComplete: () => tl.setAngle(0) });
    }
    this.armIdleHint();
  }

  protected makeTile(r: number, c: number, cell: Cell): Tile | null {
    if (!cell) return null;
    const img = this.add.image(this.px(c), this.py(r), this.tileKey(cell));
    img.setDisplaySize(this.cell, this.cell);
    return img;
  }

  // ---------- input ----------
  protected onTap(p: Phaser.Input.Pointer, fromKeyboard = false): void {
    unlockAudio();
    if (this.locked || this.overlay) return;
    this.stopIdleHint();
    this.armIdleHint();
    if (!fromKeyboard) {
      this.keyboardActive = false;
      this.keyboardCursor?.setVisible(false);
    }
    const c = Math.floor((p.worldX - this.ox) / this.cell);
    const r = Math.floor((p.worldY - this.oy) / this.cell);
    if (r < 0 || c < 0 || r >= this.level.height || c >= this.level.width) return;
    const before = this.state.movesLeft;
    const tappedKey = this.tiles[r]?.[c]?.texture.key ?? "";
    const res = applyMove(this.state, this.rng, { r, c });
    if (!res.valid) {
      this.nudge(r, c);
      sfx.nope();
      return;
    }
    if (res.groupSize > 0) sfx.pop(res.groupSize);
    // Specials: the tapped one plus chain reactions (cap the sound count).
    res.activated.slice(0, 3).forEach((a, i) => {
      const isBomb = i === 0 && res.groupSize === 0 ? tappedKey === "bomb" : this.looksLikeBomb(a, res);
      this.time.delayedCall(i * 70, () => (isBomb ? sfx.bomb() : sfx.rocket()));
    });
    if (res.created.length) this.time.delayedCall(120, () => sfx.special());
    if (res.groupSize >= 5) this.comboText(this.px(c), this.py(r), res.groupSize);
    track("move", { level: this.level.id, r, c, group: res.groupSize, cleared: res.cleared.length, movesLeft: this.state.movesLeft });
    if (res.created.length) track("special_created", { level: this.level.id, kind: res.created[0]!.cell?.kind ?? "?" });
    if (res.groupSize >= 8 || res.cleared.length >= 15) track("moment", { level: this.level.id, type: "combo", size: res.cleared.length });
    void before;
    this.animate(res);
  }

  protected nudge(r: number, c: number): void {
    const tl = this.tiles[r]?.[c];
    if (!tl) return;
    this.tweens.add({ targets: tl, angle: { from: -6, to: 6 }, duration: 60, yoyo: true, repeat: 1, onComplete: () => tl.setAngle(0) });
  }

  // ---------- animation ----------
  protected animate(res: MoveResult): void {
    this.locked = true;
    this.clearMarkers();
    const clearDur = 140;
    this.flyToGoals(res);
    // 1. clear
    for (const p of res.cleared) {
      const tl = this.tiles[p.r]![p.c];
      if (!tl) continue;
      this.tiles[p.r]![p.c] = null;
      this.burst(tl.x, tl.y, tl.texture.key);
      const w = tl.displayWidth;
      this.tweens.chain({
        targets: tl,
        tweens: [
          { displayWidth: w * 1.22, displayHeight: w * 1.22, duration: 60, ease: "Quad.Out" },
          { displayWidth: 0, displayHeight: 0, alpha: 0, duration: clearDur - 60, ease: "Back.In", onComplete: () => tl.destroy() },
        ],
      });
    }
    for (const p of res.activated) {
      this.cameras.main.shake(110, 0.005);
      this.streak(p, res);
    }
    if (res.cleared.length >= 12) this.cameras.main.flash(120, 255, 255, 255, false);

    // 2. specials created (pop in at tapped cell, before gravity)
    this.time.delayedCall(clearDur * 0.6, () => {
      for (const cr of res.created) {
        const tl = this.makeTile(cr.pos.r, cr.pos.c, cr.cell)!;
        tl.setScale(0);
        this.tiles[cr.pos.r]![cr.pos.c] = tl;
        this.tweens.add({ targets: tl, displayWidth: this.cell, displayHeight: this.cell, duration: 180, ease: "Back.Out" });
      }
    });

    // 3. falls + spawns
    const fallStart = clearDur + 40;
    let maxDur = 0;
    this.time.delayedCall(fallStart, () => {
      // move sprites in the tiles matrix according to falls (process bottom-up per column to avoid overwrite)
      const moved: { tl: Tile; to: { r: number; c: number } }[] = [];
      for (const f of res.falls) {
        const tl = this.tiles[f.from.r]![f.from.c];
        if (!tl) continue;
        this.tiles[f.from.r]![f.from.c] = null;
        moved.push({ tl, to: f.to });
      }
      for (const m of moved) {
        this.tiles[m.to.r]![m.to.c] = m.tl;
        const dist = Math.abs(m.to.r - Math.round((m.tl.y - this.oy - this.cell / 2) / this.cell));
        const dur = 140 + dist * 55;
        maxDur = Math.max(maxDur, dur);
        this.tweens.add({ targets: m.tl, y: this.py(m.to.r), duration: dur, ease: "Cubic.In", onComplete: () => this.land(m.tl) });
      }
      // spawns: count per column to stack them above the board
      const perCol = new Map<number, number>();
      for (const s of [...res.spawned].sort((a, b) => b.pos.r - a.pos.r)) {
        const n = perCol.get(s.pos.c) ?? 0;
        perCol.set(s.pos.c, n + 1);
        const tl = this.makeTile(s.pos.r, s.pos.c, s.cell)!;
        tl.y = this.oy - this.cell * (n + 1) + this.cell / 2;
        this.tiles[s.pos.r]![s.pos.c] = tl;
        const dist = s.pos.r + n + 1;
        const dur = 140 + dist * 55;
        maxDur = Math.max(maxDur, dur);
        this.tweens.add({ targets: tl, y: this.py(s.pos.r), duration: dur, ease: "Cubic.In", onComplete: () => this.land(tl) });
      }
      this.time.delayedCall(maxDur + 110, () => this.afterMove(res));
    });
  }

  /**
   * The "collect" beat of blast games: cleared tiles that count for a goal fly into its chip, which bumps
   * as they land. Call before the cleared tiles are dropped.
   */
  protected flyToGoals(res: MoveResult): void {
    if (!this.goalChips.length) return;
    let n = 0;
    for (const p of res.cleared) {
      if (n >= FLY_MAX) break;
      const tl = this.tiles[p.r]?.[p.c];
      if (!tl) continue;
      const key = tl.texture.key;
      const gi = this.state.progress.findIndex(({ goal }) =>
        goal.type === "color" ? key === `c${goal.color}` : goal.type === "crate" ? key.startsWith("crate") : false,
      );
      const target = this.goalChips[gi];
      if (!target) continue;
      const s = this.add.image(tl.x, tl.y, key).setDisplaySize(this.cell * 0.8, this.cell * 0.8).setDepth(35);
      this.tweens.add({
        targets: s,
        x: target.x,
        y: target.y,
        displayWidth: 40,
        displayHeight: 40,
        duration: 340,
        delay: 40 + n * 18,
        ease: "Cubic.In",
        onComplete: () => {
          s.destroy();
          const chip = this.goalChips[gi]; // the HUD may have redrawn its chips meanwhile
          if (!chip?.active) return;
          this.tweens.killTweensOf(chip);
          chip.setDisplaySize(60, 60);
          this.tweens.add({ targets: chip, displayWidth: 48, displayHeight: 48, duration: 160, ease: "Back.Out" });
        },
      });
      n++;
    }
  }

  /** Landing squash: tiles compress on impact and spring back. */
  protected land(tl: Tile): void {
    if (!tl.active) return;
    const w = this.cell;
    tl.setDisplaySize(w * 1.12, w * 0.86);
    this.tweens.add({ targets: tl, displayWidth: w, displayHeight: w, duration: 110, ease: "Back.Out" });
  }

  /** Heuristic from the cleared set: a bomb clears a compact 3x3, a rocket clears a full line. */
  protected looksLikeBomb(p: { r: number; c: number }, res: MoveResult): boolean {
    const rowHits = res.cleared.filter((q) => q.r === p.r).length;
    const colHits = res.cleared.filter((q) => q.c === p.c).length;
    return rowHits < this.level.width - 1 && colHits < this.level.height - 1;
  }

  /** Rocket streak (row/column flash) or bomb ring at an activated special. */
  protected streak(p: { r: number; c: number }, res: MoveResult): void {
    const x = this.px(p.c);
    const y = this.py(p.r);
    const rowHits = res.cleared.filter((q) => q.r === p.r).length;
    const colHits = res.cleared.filter((q) => q.c === p.c).length;
    if (this.looksLikeBomb(p, res)) {
      const ring = this.add.circle(x, y, this.cell * 0.6).setStrokeStyle(8, 0xffffff, 0.9).setFillStyle(0xffffff, 0.12);
      this.tweens.add({ targets: ring, scale: 3.2, alpha: 0, duration: 320, ease: "Quad.Out", onComplete: () => ring.destroy() });
      return;
    }
    const horizontal = rowHits >= colHits;
    const bar = horizontal
      ? this.add.rectangle(W / 2, y, this.cell * this.level.width + 20, this.cell * 0.55, 0xffffff, 0.85)
      : this.add.rectangle(x, this.oy + (this.cell * this.level.height) / 2, this.cell * 0.55, this.cell * this.level.height + 20, 0xffffff, 0.85);
    bar.setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({ targets: bar, alpha: 0, scaleY: horizontal ? 0.2 : 1, scaleX: horizontal ? 1 : 0.2, duration: 260, ease: "Quad.Out", onComplete: () => bar.destroy() });
  }

  /** Floating praise for big groups. */
  protected comboText(x: number, y: number, size: number): void {
    const label = size >= 10 ? t("combo_huge") : size >= 8 ? t("combo_big") : t("combo_good");
    const txt = this.add
      .text(x, y, `${label}\n+${size}`, { fontFamily: FONT, fontSize: size >= 8 ? "44px" : "34px", color: "#ffffff", fontStyle: "bold", align: "center", stroke: "#1b1f3a", strokeThickness: 8 })
      .setOrigin(0.5)
      .setScale(0.4)
      .setDepth(50);
    this.tweens.chain({
      targets: txt,
      tweens: [
        { scale: 1.1, duration: 140, ease: "Back.Out" },
        { y: y - 90, alpha: 0, duration: 520, ease: "Quad.In", delay: 160, onComplete: () => txt.destroy() },
      ],
    });
  }

  /** Confetti from the top on a win. */
  protected confetti(): void {
    for (let i = 0; i < 36; i++) {
      const x = 60 + ((i * 97) % 600);
      const s = this.add.image(x, -20, "spark").setTint(COLORS[i % COLORS.length]!).setScale(0.8 + (i % 3) * 0.3).setDepth(40);
      this.tweens.add({
        targets: s,
        y: this.scale.height + 40,
        x: x + ((i % 5) - 2) * 40,
        angle: 360 * ((i % 2) * 2 - 1),
        duration: 1400 + (i % 7) * 120,
        delay: (i % 9) * 40,
        ease: "Quad.In",
        onComplete: () => s.destroy(),
      });
    }
  }

  protected burst(x: number, y: number, key: string): void {
    const tint = key.startsWith("c") && key.length === 2 ? COLORS[Number(key[1])]! : 0xffffff;
    for (let i = 0; i < 7; i++) {
      const s = this.add.image(x, y, "spark").setTint(tint).setScale(0.6 + Math.random() * 0.7).setDepth(30);
      const a = Math.random() * Math.PI * 2;
      const d = 30 + Math.random() * 50;
      this.tweens.add({
        targets: s,
        x: x + Math.cos(a) * d,
        y: y + Math.sin(a) * d + 20,
        alpha: 0,
        scale: 0,
        duration: 260 + Math.random() * 120,
        ease: "Quad.Out",
        onComplete: () => s.destroy(),
      });
    }
  }

  protected afterMove(res: MoveResult): void {
    if (res.shuffled) {
      sfx.shuffle();
      this.hint.setText(t("shuffle"));
      this.buildTiles();
      this.time.delayedCall(1200, () => this.hint.setText(t("tap_hint")));
    }
    // Resync any drift (defensive): rebuild tiles whose texture mismatches the state.
    for (let r = 0; r < this.level.height; r++)
      for (let c = 0; c < this.level.width; c++) {
        const cell = this.state.grid[r]![c] ?? null;
        const tl = this.tiles[r]![c];
        const key = this.tileKey(cell);
        if (!tl || tl.texture.key !== key) {
          tl?.destroy();
          this.tiles[r]![c] = this.makeTile(r, c, cell);
        } else {
          tl.setPosition(this.px(c), this.py(r));
        }
      }
    this.updateHud();
    this.locked = false;
    this.drawMarkers();
    this.armIdleHint();
    if (this.state.status === "won") this.onWon();
    else if (this.state.status === "lost") this.onLost();
  }

  // ---------- end states ----------
  protected onWon(): void {
    const secs = Math.round((Date.now() - this.startedAt) / 1000);
    const clutch = this.state.movesLeft === 0;
    const n = this.index + 1;
    const stars = classicStars(this.level, this.state.movesLeft);
    track("level_end", { level: this.level.id, result: "won", secs, movesLeft: this.state.movesLeft, best: this.state.bestGroup, stars });
    if (clutch) track("moment", { level: this.level.id, type: "clutch_win" });
    delete this.progress.fails[this.level.id];
    const imgId = this.level.imageId ?? this.level.id;
    if (!this.progress.revealed.includes(imgId)) this.progress.revealed.push(imgId);
    this.progress.levelIndex = Math.max(this.progress.levelIndex, n);
    saveProgress(this.progress);
    const outcome = recordWin(MAP_ID, n, stars);
    sfx.win();
    const name = this.pictureName();
    const named = name ? `${name} ${t("won_sub_named")}` : t("won_sub");
    this.celebrate(() => {
      this.confetti();
      this.showOverlay(t("won_title"), `${starLine(stars)}\n${named}`, [
        { label: t("share"), color: 0x1982c4, onClick: () => this.share() },
        { label: mapText("continue", lang), color: 0x8ac926, onClick: () => openMap(this, MAP_ID, afterWin(n, outcome)) },
      ]);
    });
  }

  /**
   * Win beat before the card: the moves left (up to BONUS_SHOTS) fly from the counter onto the board as
   * fireworks. Pure show: stars are already decided from the moves left (classicStars).
   */
  protected celebrate(done: () => void): void {
    this.locked = true;
    this.stopIdleHint();
    const shots = Math.min(this.state.movesLeft, BONUS_SHOTS);
    const from = { x: this.hudMoves.x - this.hudMoves.displayWidth / 2, y: this.hudMoves.y };
    for (let i = 0; i < shots; i++) {
      this.time.delayedCall(i * 130, () => {
        const r = Math.floor(Math.random() * this.level.height);
        const c = Math.floor(Math.random() * this.level.width);
        const x = this.px(c);
        const y = this.py(r);
        this.hudMoves.setText(String(this.state.movesLeft - i - 1));
        const s = this.add.image(from.x, from.y, "spark").setScale(2.2).setTint(COLORS[i % COLORS.length]!).setDepth(36);
        this.tweens.add({
          targets: s,
          x,
          y,
          duration: 240,
          ease: "Quad.In",
          onComplete: () => {
            s.destroy();
            const tl = this.tiles[r]?.[c];
            this.burst(x, y, tl?.texture.key ?? "spark");
            if (tl) {
              tl.setDisplaySize(this.cell * 1.25, this.cell * 1.25);
              this.tweens.add({ targets: tl, displayWidth: this.cell, displayHeight: this.cell, duration: 160, ease: "Back.Out" });
            }
            sfx.pop(3 + i);
          },
        });
      });
    }
    this.time.delayedCall(shots ? shots * 130 + 380 : 0, done);
  }

  protected onLost(): void {
    const secs = Math.round((Date.now() - this.startedAt) / 1000);
    const fails = (this.progress.fails[this.level.id] ?? 0) + 1;
    this.progress.fails[this.level.id] = fails;
    saveProgress(this.progress);
    track("level_end", { level: this.level.id, result: "lost", secs, fails, reveal: revealFraction(this.state.progress) });
    if (revealFraction(this.state.progress) >= 0.9) track("moment", { level: this.level.id, type: "near_miss" });
    sfx.lose();
    const buttons = [
      {
        label: t("retry"),
        color: 0x1982c4,
        onClick: () => {
          track("retry", { level: this.level.id, fails });
          this.startLevel(this.index);
        },
      },
    ];
    // H4: after 3 failed attempts the level can be skipped, free, always.
    if (fails >= 3) {
      buttons.push({
        label: t("skip"),
        color: 0x6a4c93,
        onClick: () => {
          track("skip_used", { level: this.level.id, fails });
          delete this.progress.fails[this.level.id];
          const n = this.index + 1;
          this.progress.levelIndex = Math.max(this.progress.levelIndex, n);
          saveProgress(this.progress);
          openMap(this, MAP_ID, afterSkip(n, recordSkip(MAP_ID, n)));
        },
      });
    }
    this.showOverlay(t("lost_title"), fails >= 3 ? t("lost_sub") : `${t("lost_sub")}\n${t("skip_hint")}`, buttons);
  }

  protected showOverlay(
    title: string,
    sub: string,
    buttons: { label: string; color: number; onClick: () => void }[],
  ): void {
    this.clearMarkers();
    this.stopIdleHint();
    const c = this.add.container(0, 0);
    const H = this.scale.height;
    const dim = this.add.rectangle(W / 2, H / 2, W * 3, H, 0x000000, 0.55).setInteractive();
    const panel = this.add.rectangle(W / 2, H / 2, 600, 420, 0x262b4d).setStrokeStyle(4, 0x3a4170);
    const tt = this.add
      .text(W / 2, H / 2 - 130, title, { fontFamily: FONT, fontSize: "52px", color: "#ffffff", fontStyle: "bold" })
      .setOrigin(0.5);
    const st = this.add
      .text(W / 2, H / 2 - 60, sub, { fontFamily: FONT, fontSize: "24px", color: "#b8bee6", align: "center" })
      .setOrigin(0.5);
    c.add([dim, panel, tt, st]);
    const totalW = buttons.length * 250 + (buttons.length - 1) * 20;
    buttons.forEach((b, i) => {
      const x = W / 2 - totalW / 2 + 125 + i * 270;
      const y = H / 2 + 100;
      const bg = this.add.rectangle(x, y, 250, 84, b.color).setInteractive({ useHandCursor: true });
      const label = this.add
        .text(x, y, b.label, { fontFamily: FONT, fontSize: "28px", color: "#ffffff", fontStyle: "bold" })
        .setOrigin(0.5);
      bg.on("pointerdown", () => {
        this.tweens.add({ targets: [bg, label], scale: 0.94, duration: 60, yoyo: true, onComplete: b.onClick });
      });
      c.add([bg, label]);
    });
    c.setScale(0.9).setAlpha(0);
    this.tweens.add({ targets: c, scale: 1, alpha: 1, duration: 180, ease: "Back.Out" });
    this.overlay = c;
  }

  // ---------- share ----------
  protected async share(): Promise<void> {
    track("share_click", { level: this.level.id });
    const name = this.pictureName();
    const text = name
      ? t("share_text_named", { name, n: this.index + 1 })
      : t("share_text", { n: this.index + 1 });
    const url = location.origin + location.pathname;
    try {
      const file = await this.renderShareImage();
      const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
      if (file && nav.share && nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], text, title: "Mozaik" });
      } else if (nav.share) {
        await nav.share({ text: `${text} ${url}`, title: "Mozaik" });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
      }
      track("share_done", { level: this.level.id });
    } catch {
      /* user cancelled */
    }
  }

  /** 1080x1080 PNG: the revealed pixel image on brand background — the social card. */
  protected renderShareImage(): Promise<File | null> {
    return new Promise((resolve) => {
      const cv = document.createElement("canvas");
      cv.width = 1080;
      cv.height = 1080;
      const ctx = cv.getContext("2d");
      if (!ctx) return resolve(null);
      ctx.fillStyle = "#1b1f3a";
      ctx.fillRect(0, 0, 1080, 1080);
      const px = 720 / this.image.size;
      const x0 = 180;
      const y0 = 120;
      if (this.hdImg) ctx.drawImage(this.hdImg, x0, y0, 720, 720);
      else for (let i = 0; i < this.image.pixels.length; i++) {
        const col = this.image.pixels[i]!;
        if (col < 0) continue;
        ctx.fillStyle = this.image.palette[col]!;
        ctx.fillRect(x0 + (i % this.image.size) * px, y0 + Math.floor(i / this.image.size) * px, px + 0.5, px + 0.5);
      }
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 56px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`Mozaik · ${t("level", { n: this.index + 1 })}`, 540, 940);
      ctx.fillStyle = "#8f96c2";
      ctx.font = "32px system-ui, sans-serif";
      ctx.fillText(t("no_ads"), 540, 1000);
      cv.toBlob((blob) => resolve(blob ? new File([blob], "mozaik.png", { type: "image/png" }) : null), "image/png");
    });
  }
}
